import {
  alpha,
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { COLOR_MAIN, COLOR_WHITE } from "../../data/colors/Colors";
import { MenuBookRounded, SchoolRounded } from "@mui/icons-material";
import { useState } from "react";
import { SchoolType } from "../../domain/model/User";
import { useTranslation } from "react-i18next";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: alpha(COLOR_WHITE, 0.4),
  padding: "4px",
  // 1. Set a high border radius for a pill/circular shape
  borderRadius: "50px",
  border: "1px solid #e2e8f0",
  display: "flex",
  gap: "4px", // 2. Add spacing between the tabs

  "& .MuiToggleButtonGroup-grouped": {
    border: "0 !important", // Remove default borders between buttons
    // 3. Match the pill shape for the buttons too
    borderRadius: "50px !important",
    padding: "8px 12px",
    textTransform: "none",
    fontWeight: 600,
    display: "flex",
    gap: 8,
    transition: "all 0.2s ease-in-out",
    color: "#64748b",

    // --- MOBILE FONT SIZE ---
    fontSize: "0.75rem", // Shrink to 12px roughly

    // --- TABLET/DESKTOP FONT SIZE ---
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.85rem",
      padding: "8px 16px",
      gap: 8,
    },

    "&.Mui-selected": {
      backgroundColor: COLOR_MAIN,
      color: COLOR_WHITE,
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      "&:hover": {
        backgroundColor: alpha(COLOR_MAIN, 0.9),
        color: COLOR_WHITE,
      },
    },

    // Smooth hover for the unselected tab
    "&:hover": {
      backgroundColor: alpha(COLOR_MAIN, 0.05),
      color: COLOR_MAIN,
    },
    // Handle icon size specifically for mobile
    "& .MuiSvgIcon-root": {
      fontSize: "1.1rem", // Slightly smaller icons
      [theme.breakpoints.up("sm")]: {
        fontSize: "1.25rem",
      },
    },
  },
}));

export function SchoolTypeToggleGroup({
  defaultValue = SchoolType.PRIMARY,
  onTypeChanged,
}: {
  defaultValue?: SchoolType;
  onTypeChanged: (type: SchoolType) => void;
}) {
  const { t } = useTranslation();
  const [schoolType, setSchoolType] = useState<SchoolType>(defaultValue);

  const handleChange = (_: any, newType: any) => {
    if (newType !== null) {
      setSchoolType(newType);
      onTypeChanged(newType);
    }
  };

  return (
    <Stack
      sx={{
        alignItems: "start",
        width: "100%",
      }}
    >
      <StyledToggleButtonGroup
        value={schoolType}
        exclusive
        onChange={handleChange}
        aria-label="school level"
      >
        <ToggleButton
          value={SchoolType.PRIMARY}
          aria-label={SchoolType.PRIMARY}
        >
          <MenuBookRounded />
          {t("primary_school")}
        </ToggleButton>

        <ToggleButton
          value={SchoolType.MIDDLE_AND_HIGH_SCHOOL}
          aria-label={SchoolType.MIDDLE_AND_HIGH_SCHOOL}
        >
          <SchoolRounded />
          {t("secondary_school")}
        </ToggleButton>
      </StyledToggleButtonGroup>
    </Stack>
  );
}
